/* TanStack Query key factory.
 *
 * Plan §"React production standards / data-fetching" mandates that every
 * cache key is namespaced by tenantCode — e.g.
 *   `['customer', tenantCode, 'session']`.
 *
 * This file is the single source of truth. Cross-feature hooks must import
 * from here so a future "switch tenant" affordance can invalidate cleanly.
 */

export const customerQueryKeys = {
  all: (tenantCode: string) => ['customer', tenantCode] as const,

  session: (tenantCode: string) =>
    ['customer', tenantCode, 'session'] as const,

  donationProject: (tenantCode: string, code: string) =>
    ['customer', tenantCode, 'donationProject', code] as const,

  donationProjectsList: (tenantCode: string) =>
    ['customer', tenantCode, 'donationProjects'] as const,

  transaction: (tenantCode: string, paymentId: string) =>
    ['customer', tenantCode, 'transaction', paymentId] as const,

  checkoutSession: (tenantCode: string, sessionId: string) =>
    ['customer', tenantCode, 'checkoutSession', sessionId] as const,

  paymentStatus: (tenantCode: string, paymentId: string) =>
    ['customer', tenantCode, 'paymentStatus', paymentId] as const,

  resolvePostKioskToken: (tenantCode: string, token: string) =>
    ['customer', tenantCode, 'resolvePostKioskToken', token] as const,

  shopProducts: (tenantCode: string, kioskId: number) =>
    ['customer', tenantCode, 'shop', 'products', kioskId] as const,

  shopConfig: (tenantCode: string, kioskId: number) =>
    ['customer', tenantCode, 'shop', 'config', kioskId] as const,

  shopKioskBySlug: (tenantCode: string, slug: string) =>
    ['customer', tenantCode, 'shop', 'kioskBySlug', slug] as const,

  /** Prefix-invalidate all slug lookups for a tenant (directory / branding SSE). */
  shopKioskBySlugPrefix: (tenantCode: string) =>
    ['customer', tenantCode, 'shop', 'kioskBySlug'] as const,

  shopKioskById: (tenantCode: string, kioskId: number) =>
    ['customer', tenantCode, 'shop', 'kioskById', kioskId] as const,

  /** Prefix-invalidate all by-id lookups for a tenant (branding SSE). */
  shopKioskByIdPrefix: (tenantCode: string) =>
    ['customer', tenantCode, 'shop', 'kioskById'] as const,

  kioskPickupPoints: (tenantCode: string, kioskId: number) =>
    ['customer', tenantCode, 'kioskPickupPoints', kioskId] as const,

  pickupSlots: (
    tenantCode: string,
    kioskId: number,
    pickupPointId: number,
    rangeDay: string
  ) =>
    ['customer', tenantCode, 'pickupSlots', kioskId, pickupPointId, rangeDay] as const,

  /** Invalidate all account-scoped caches after login/logout/register. */
  accountPrefix: (tenantCode: string) =>
    ['customer', tenantCode, 'account'] as const,

  /** Account reads are scoped per customer — never cache by tenant alone. */
  accountMe: (tenantCode: string, customerId: number) =>
    ['customer', tenantCode, 'account', customerId, 'me'] as const,

  /** Internal `/me` validation envelope — never consumed by `useAccountMe`. */
  accountMeValidation: (tenantCode: string, customerId: number) =>
    ['customer', tenantCode, 'account', customerId, 'me-validation'] as const,

  accountDonations: (
    tenantCode: string,
    customerId: number,
    filters: { year?: number; cursor?: string | null; limit?: number }
  ) => ['customer', tenantCode, 'account', customerId, 'donations', filters] as const,

  accountDonationsSummary: (tenantCode: string, customerId: number, year?: number) =>
    ['customer', tenantCode, 'account', customerId, 'donations', 'summary', year ?? null] as const,

  accountPayments: (
    customerId: number,
    filters: {
      purposeType?: 'PRODUCT' | 'DONATION';
      status?: string;
      cursor?: string | null;
      limit?: number;
    }
  ) => ['customer', 'account', customerId, 'payments', filters] as const,

  accountReceiptLocales: (tenantCode: string, customerId: number) =>
    ['customer', tenantCode, 'account', customerId, 'receiptLocales'] as const,

  accountEmailLocale: (tenantCode: string, customerId: number) =>
    ['customer', tenantCode, 'account', customerId, 'emailLocale'] as const,

  accountMemberships: (tenantCode: string, customerId: number) =>
    ['customer', tenantCode, 'account', customerId, 'memberships'] as const,

  /** Platform chrome GET `/api/v1/customer-account/memberships` (identity JWT). */
  platformAccountMemberships: (customerId: number) =>
    ['customer', 'platform', 'account', customerId, 'memberships'] as const,

  loyaltyWallet: (tenantCode: string, customerId: number) =>
    ['customer', tenantCode, 'loyalty', 'wallet', customerId] as const,
  loyaltyCoupons: (tenantCode: string, customerId: number) =>
    ['customer', tenantCode, 'loyalty', 'coupons', customerId] as const,

  loyaltyProfilingConsent: (tenantCode: string, customerId: number) =>
    ['customer', tenantCode, 'loyalty', 'profilingConsent', customerId] as const,

  promoRewards: (tenantCode: string, customerId: number) =>
    ['customer', tenantCode, 'promo', 'rewards', customerId] as const,

  promoEvents: (tenantCode: string, customerId: number) =>
    ['customer', tenantCode, 'promo', 'events', customerId] as const,

  promoProgressHints: (tenantCode: string, customerId: number) =>
    ['customer', tenantCode, 'promo', 'progressHints', customerId] as const,

  promoPreviewPricing: (
    tenantCode: string,
    scope: {
      customerId: number;
      sessionId: string;
      items: readonly unknown[];
      activatedPromoRewardId: string | null;
    },
  ) =>
    [
      'customer',
      tenantCode,
      'promo',
      'previewPricing',
      scope.customerId,
      scope.sessionId,
      scope.activatedPromoRewardId,
      scope.items,
    ] as const,

  checkoutPricingPreview: (
    tenantCode: string,
    scope: {
      customerId: number;
      sessionId: string;
      items: readonly unknown[];
      activatedPromoRewardId: string | null;
      activatedCouponId: string | null;
    },
  ) =>
    [
      'customer',
      tenantCode,
      'checkout',
      'pricingPreview',
      scope.customerId,
      scope.sessionId,
      scope.activatedPromoRewardId,
      scope.activatedCouponId,
      scope.items,
    ] as const,

  /** Platform L3 contacts — not tenant-scoped. */
  salesPointContacts: (customerKey: number | string) =>
    ['customer', 'salesPointContacts', customerKey] as const,

  /** Platform L3 Taška★ offer@SP bag — not tenant-scoped (Wave 5 / NEW G8). */
  offerFavorites: (customerKey: number | string) =>
    ['customer', 'offerFavorites', customerKey] as const,

  /** Platform L3 map custom views — not tenant-scoped (ADR-MV-05). */
  mapCustomViews: (customerKey: number | string) =>
    ['customer', 'mapCustomViews', customerKey] as const,

  /** Unscoped public tenant directory (Organizace landing). */
  publicCustomerTenants: (apiBaseUrl: string) =>
    ['public', 'customer-tenants', apiBaseUrl] as const,

  /**
   * Public sales-point directory (platform /shops or tenant-scoped).
   * `apiSort` is the mapped wire sort (`favoriteCountDesc` | null when omitted).
   */
  publicCustomerSalesPoints: (
    apiBaseUrl: string,
    filterTenantCode: string | null,
    apiSort: 'favoriteCountDesc' | null = null,
  ) =>
    ['public', 'customer-sales-points', apiBaseUrl, filterTenantCode, apiSort] as const,

  /**
   * Drained Obchody SP allowlist for Favorites / Purchases.
   * Shares `['public','customer-sales-points',apiBaseUrl]` prefix so
   * `invalidatePublicCustomerSalesPointsCache` refreshes Favorites with `/shops`.
   */
  publicCustomerSalesPointListableAllowlist: (apiBaseUrl: string) =>
    ['public', 'customer-sales-points', apiBaseUrl, 'listable-allowlist'] as const,

  /**
   * Browse with-q Obchody / Vše — full SP drain then client-filter (D22; no name API).
   * Shares `['public','customer-sales-points',apiBaseUrl]` prefix for SSE/cache invalidate.
   */
  publicCustomerSalesPointsBrowseDrain: (apiBaseUrl: string) =>
    ['public', 'customer-sales-points', apiBaseUrl, 'browse-drain'] as const,

  publicDiscoverySearch: (
    apiBaseUrl: string,
    filters: {
      q: string;
      platformCategory: string | null;
      radiusMeters: number | null;
      sort: 'relevance' | 'distance';
      hasLocation: boolean;
      locationIdentity: string | null;
      locale: string;
    },
  ) => ['public', 'discovery', 'search', apiBaseUrl, filters] as const,

  publicDiscoveryPlatformCategories: (apiBaseUrl: string, locale: string) =>
    ['public', 'discovery', 'platform-categories', apiBaseUrl, locale] as const,

  publicDiscoveryPlatformCategoriesTree: (apiBaseUrl: string, locale: string) =>
    ['public', 'discovery', 'platform-categories', 'tree', apiBaseUrl, locale] as const,

  publicDiscoveryHomeCategories: (apiBaseUrl: string, locale: string) =>
    ['public', 'discovery', 'home-categories', apiBaseUrl, locale] as const,

  accountRecurringSetups: (tenantCode: string, customerId: number) =>
    ['customer', tenantCode, 'account', customerId, 'recurringSetups'] as const,

  marketingAnalyticsConsentPrompt: (tenantCode: string) =>
    ['customer', tenantCode, 'consent', 'marketingAnalyticsPrompt'] as const,

  surfaceEntitlement: (tenantCode: string) =>
    ['customer', tenantCode, 'surfaceEntitlement'] as const,

  featureFlags: (tenantCode: string) =>
    ['customer', tenantCode, 'featureFlags'] as const,

  /** B01 / H09-TECH — public APPROVED seller legal identity. */
  sellerLegal: (tenantCode: string) =>
    ['customer', tenantCode, 'sellerLegal'] as const,

  /** GoPay MoR — published tenant_store_policies links (may 404 until BE lands). */
  storePolicies: (tenantCode: string) =>
    ['customer', tenantCode, 'storePolicies'] as const,

  /** B03/S15 — sealed durable order confirmation (immutable artifact). */
  durableConfirmation: (transactionId: number) =>
    ['customer', 'durableConfirmation', transactionId] as const,

  /** G9 — eligible DeliveryPolicy for sales point (policy null when shippingLightEnabled OFF). */
  shippingMethods: (tenantCode: string, salesPointId: number) =>
    ['customer', tenantCode, 'shippingMethods', salesPointId] as const,

  /** Alias — same cache as shippingMethods (DeliveryPolicy eligible get). */
  deliveryPolicyEligible: (tenantCode: string, salesPointId: number) =>
    ['customer', tenantCode, 'shippingMethods', salesPointId] as const,

  /** Membership-scoped saved shipping address (shippingAddressJson). */
  membershipShippingAddress: (tenantCode: string) =>
    ['customer', tenantCode, 'membershipShippingAddress'] as const,

  /** Live CARD offer probe — GetGatewayMethods for a sales point × currency × purpose. */
  gatewayMethods: (
    tenantCode: string,
    salesPointId: number,
    currency?: string,
    purpose?: string,
  ) =>
    currency != null && purpose != null
      ? (['customer', tenantCode, 'gatewayMethods', salesPointId, currency, purpose] as const)
      : (['customer', tenantCode, 'gatewayMethods', salesPointId] as const),

  customerAccountDailyCashRemaining: (tenantCode: string, customerId: number) =>
    ['customer', tenantCode, 'dailyCashRemaining', customerId] as const,

  /** Server Self-Scan basket SoT (never useShopCart / localStorage cart). */
  selfScanBasket: (tenantCode: string, customerId: number, publicId: string) =>
    ['customer', tenantCode, 'selfScan', 'basket', customerId, publicId] as const,

  selfScanSettings: (tenantCode: string, salesPointId: number | null) =>
    ['customer', tenantCode, 'selfScan', 'settings', salesPointId ?? 0] as const,

  selfScanHistory: (tenantCode: string, customerId: number, salesPointId: number | null) =>
    ['customer', tenantCode, 'selfScan', 'history', customerId, salesPointId ?? 0] as const,
};

export type CustomerQueryKey = ReturnType<
  (typeof customerQueryKeys)[keyof typeof customerQueryKeys]
>;
